import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import ClientSourceAddEditModal from "pages/AllCommonModal/ClientSourceAddEditModal/client-source-add-edit-modal";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useClientSourceContext } from "libs/context/ClientSourceContext";
import { submitClientSourceFilterData } from "model/ClientSourceModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const SourceSection = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchClientSourceContextData, clientSourceContextData } = useClientSourceContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("ClientSource");
  const initialFilterFetchedRef = useRef(false);
  const [clientSourceAddEditModalOpen, setClientSourceAddEditModalOpen] = useState(false);
  const [modal1, setModal1] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceData, setSourceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const toggleViewModal = () => setModal1(!modal1);
  const filteredData = useMemo(() => sourceData, [sourceData]);

  const clientSourceActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.ClientSourceAddAccess),
      canEdit: Boolean(permissionData?.ClientSourceUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyClientSourceRowAction = useMemo(
    () => clientSourceActionPermissions.canEdit,
    [clientSourceActionPermissions]
  );

  useEffect(() => {
    fetchClientSourceContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getClientSourceList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = clientSourceContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: pascalToCamel(col?.field),
      type: col?.type,
    }));
    setTableMetaData(valueReModified || []);
  }, [clientSourceContextData]);

  const getClientSourceList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitClientSourceFilterData(payload);
    const responseData = response?.data || {};
    // const listData = responseData?.records || [];
    const valueReModified = responseData?.map((item = {}) => ({
      ...item,
      id: item?.id || item?._id || "",
      name: item?.name || item?.title || "",
      status: item?.status || "deactive",
    }));

    if (response) {
      setSourceData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(payload);
    } else {
      setSourceData([]);
    }
    setLoading(false);
  };

  const handleEdit = (row) => {
    setItemId(row?.id);
    setClientSourceAddEditModalOpen(true);
  };

  const handleFilterSubmit = async (data) => {
    await getClientSourceList({
      ...data,
      nameContains: requestPayload?.name || "",
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    const nextPayload = {
      ...requestPayload,
      pageNumber: 1,
      nameContains: trimmedValue,
    };
    await getClientSourceList(nextPayload);
  };

  const handlePageChange = async (page) => {
    await getClientSourceList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getClientSourceList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Source</h5>

                {clientSourceActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setClientSourceAddEditModalOpen(true);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyClientSourceRowAction}
                  onEdit={
                    clientSourceActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isEditAccess={clientSourceActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"ClientSource"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>

          <ClientSourceAddEditModal
            setClientSourceAddEditModalOpen={setClientSourceAddEditModalOpen}
            clientSourceAddEditModalOpen={clientSourceAddEditModalOpen}
            getClientSourceList={getClientSourceList}
            itemId={itemId}
          />

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Source"
            handleSubmit={handleFilterSubmit}
            filterData={clientSourceContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

SourceSection.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(SourceSection);
